
import {
  computed,
  watch,
  defineComponent,
  reactive,
  toRefs,
  onMounted,
  ref,
  PropType,
  onUnmounted,
} from "vue";
import debounce from "lodash.debounce";
import scrollIntoView from "scroll-into-view-if-needed";
import ResizeObserver from "resize-observer-polyfill";
import Tab from "./Tab.vue";
import TabScroller from "./TabScroller.vue";
import TabGlider from "./TabGlider.vue";
import "./tabs.scss";

export default defineComponent({
  name: "adm-tabs",
  components: {
    Tab,
    TabScroller,
    TabGlider,
  },
  emits: ["update:modelValue"],
  props: {
    tabs: {
      type: Array as PropType<Array<{ label: string; value: string }>>,
      required: true,
    },
    modelValue: {
      type: String,
    },
    minWidth: {
      type: Boolean,
      default: true,
    },
  },
  setup: function (props, { emit }) {
    const root = ref<HTMLElement | null>(null);
    const tabRefs = reactive<Record<string, HTMLElement>>({});
    const { minWidth, modelValue } = toRefs(props);
    const glider = reactive({
      position: 0,
      width: 0,
    });

    let resizeObserver: ResizeObserver;

    const classes = computed(() => [
      "adm-tabs",
      {
        "adm-tabs--full-width": !minWidth.value,
      },
    ]);

    const onChange = (value: string) => {
      emit("update:modelValue", value);
    };

    const layoutGlider = debounce((needScroll = false) => {
      if (!modelValue.value || !tabRefs[modelValue.value]) {
        glider.width = 0;
        glider.position = 0;
        return;
      }

      if (needScroll) {
        scrollIntoView(tabRefs[modelValue.value], {
          behavior: "smooth",
          scrollMode: "if-needed",
          block: "nearest",
          inline: "nearest",
        });
      }

      const { offsetLeft, clientWidth } = tabRefs[modelValue.value];
      glider.width = clientWidth;
      glider.position = offsetLeft;
    }, 50);

    onMounted(() => {
      resizeObserver = new ResizeObserver(() => layoutGlider());
      layoutGlider();

      if (root.value) {
        resizeObserver.observe(root.value);
      }
    });

    onUnmounted(() => {
      resizeObserver.disconnect();
    });

    watch(modelValue, () => layoutGlider(true));

    return {
      classes,
      glider,
      tabRefs,
      root,
      onChange,
    };
  },
});
